import React, {Dispatch, FC, SetStateAction} from 'react';
import {MdDelete} from 'react-icons/md';
import {CgPen} from 'react-icons/cg';
import {
	censorObscenities,
	getDebug,
	isMasterFirebaseUser,
} from '../../../library/GeneralUse';
import {
	deleteNewsDocument,
	refreshNewsDocuments,
	updateNewsDocumentField,
} from '../../../library/NewsCollection';
import {useNewsStore} from '../../../stores/NewsStore';
import {useFirebaseAuthUserStore} from '../../../stores/FirebaseAuthUserStore';

const style = {
	admin: `flex flex-row basis-1/2   pt-1`,
	deleteButton: `text-whirlwindRed hover:text-whirlwindRedDark   opacity-90`,
	editButton: ``,
	editButtonDisabled: `text-whirlwindDisabled   opacity-65`,
	footer: `flex flex-row   pt-1`,
	time: `basis-1/2   pt-1   text-xs text-right`,
};

type NewsPostFooterProps = {
	created_at: string;
	documentId: string;
	last_edited_at: string | null;

	currentlyEditing: boolean;
	setCurrentlyEditing: Dispatch<SetStateAction<boolean>>;
	currentlyEditingDocumentId: string;
	setCurrentlyEditingDocumentId: Dispatch<SetStateAction<string>>;
};

const DEBUG = getDebug('news');

export const NewsPostFooter: FC<NewsPostFooterProps> = ({
	created_at,
	documentId,
	last_edited_at,
	currentlyEditing,
	setCurrentlyEditing,
	currentlyEditingDocumentId,
	setCurrentlyEditingDocumentId,
}) => {
	if (currentlyEditing) {
		style.editButton = `text-whirlwindGreenDark   opacity-90`;
	} else {
		style.editButton = `text-whirlwindGreen hover:text-whirlwindGreenDark   opacity-90`;
	}

	// Zustand
	const firebaseAuthUser = useFirebaseAuthUserStore(
		(state) => state.firebaseAuthUser,
	);
	const news = useNewsStore((state) => state.news);
	const setNews = useNewsStore((state) => state.setNews);

	// Component methods
	const handleDelete = async () => {
		DEBUG && console.clear();
		DEBUG && console.trace(`handleDelete()`);

		try {
			await deleteNewsDocument(documentId);

			const updatedNews = news?.filter(
				(docSnap) => docSnap.id !== documentId,
			);
			if (updatedNews && updatedNews != news) {
				setNews(updatedNews);
			}

			if (currentlyEditing && documentId === currentlyEditingDocumentId) {
				setCurrentlyEditing(false);
				setCurrentlyEditingDocumentId('');
			}
		} catch (error) {
			DEBUG && console.error(error);
		}
	};

	const handleEdit = async () => {
		DEBUG && console.clear();
		DEBUG && console.trace(`handleEdit()`);

		try {
			if (currentlyEditing) {
				setCurrentlyEditingDocumentId('');

				const newsEditTitle = (
					document.getElementById(
						`${documentId}_NewsPostTitleEditing`,
					) as HTMLInputElement
				).value;
				const newsEditContent = (
					document.getElementById(
						`${documentId}_NewsPostEditing`,
					) as HTMLInputElement
				).value;

				if (newsEditTitle && newsEditContent && firebaseAuthUser) {
					const censoredNewsEditTitle = censorObscenities(newsEditTitle);
					const censoredNewsEditContent =
						censorObscenities(newsEditContent);

					await updateNewsDocumentField(
						documentId,
						'title',
						censoredNewsEditTitle,
					);
					await updateNewsDocumentField(
						documentId,
						'content',
						censoredNewsEditContent,
					);

					await refreshNewsDocuments(4);
				} else {
					DEBUG &&
						console.log(
							`newsEditTitle is empty string or newsEditContent is empty string or firebaseAuthUser is null`,
						);
				}
			} else {
				setCurrentlyEditingDocumentId(documentId);
			}

			setCurrentlyEditing(!currentlyEditing);
		} catch (error) {
			DEBUG && console.error(error);
		}
	};

	return (
		<div className={style.footer}>
			<div
				className={style.admin}
				data-test-id={
					isMasterFirebaseUser(firebaseAuthUser?.uid)
						? `${documentId}_NewsPostAdmin_TEST`
						: ``
				}>
				{isMasterFirebaseUser(firebaseAuthUser?.uid) ? (
					<>
						<button
							className={style.deleteButton}
							data-test-id={
								isMasterFirebaseUser(firebaseAuthUser?.uid)
									? `${documentId}_NewsPostDelete_TEST`
									: ``
							}
							onClick={async () => {
								await handleDelete();
							}}>
							<MdDelete size={24} />
						</button>
						{currentlyEditingDocumentId === documentId ||
						!currentlyEditingDocumentId ? (
							<button
								className={style.editButton}
								data-test-id={
									isMasterFirebaseUser(firebaseAuthUser?.uid)
										? `${documentId}_NewsPostEdit_TEST`
										: ``
								}
								onClick={async () => {
									await handleEdit();
								}}>
								<CgPen size={20} />
							</button>
						) : (
							<button
								className={style.editButtonDisabled}
								data-test-id={
									isMasterFirebaseUser(firebaseAuthUser?.uid)
										? `${documentId}_NewsPostEdit_TEST`
										: ``
								}
								disabled>
								<CgPen size={20} />
							</button>
						)}
					</>
				) : (
					<></>
				)}
			</div>
			<span className={style.time}>
				<p>{created_at}</p>
				{last_edited_at ? <p>Last edited at {last_edited_at}</p> : <></>}
			</span>
		</div>
	);
};
